import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type Driver, type DriveStep } from 'driver.js'
import { useRxCollection } from 'rxdb-hooks'

import type { Card } from '~/db/types'
import { useSettingsStore } from '~/stores/settings'

import { resolveTourResumeStepIndex } from '../resolve-tour-resume-step-index'
import { createOnboardingTourDriver } from '../shared/create-tour-driver'
import { TourFinishConfirmDrawer } from '../shared/tour-finish-confirm-drawer'

import { createListTourSteps } from './steps-factory'

import 'driver.js/dist/driver.css'
import '../shared/driver-tour-overrides.css'

const WAIT_FOR_LAYOUT_MS = 300

export const ListTour = () => {
  const { t } = useTranslation(['onboarding', 'translation'], { nsMode: 'fallback' })
  const cardsCollection = useRxCollection<Card>('cards')
  const toursDisabled = useSettingsStore(state => state.toursDisabled)
  const listTour = useSettingsStore(state => state.tours.list)
  const disableTours = useSettingsStore(state => state.disableTours)
  const [isFinishConfirmOpen, setIsFinishConfirmOpen] = useState(false)
  const driverInstanceRef = useRef<Driver | null>(null)
  const resumeStepRef = useRef<DriveStep | null>(null)
  const isEndingTourRef = useRef(false)

  const shouldStart = !toursDisabled && !listTour.completed

  const handleKeepExploring = () => {
    if (isEndingTourRef.current) return

    setIsFinishConfirmOpen(false)
    const resumeStep = resumeStepRef.current
    if (resumeStep && driverInstanceRef.current) driverInstanceRef.current.highlight(resumeStep)
  }

  const handleEndTour = () => {
    isEndingTourRef.current = true
    setIsFinishConfirmOpen(false)
    disableTours()
    driverInstanceRef.current?.destroy()
    driverInstanceRef.current = null
  }

  useEffect(() => {
    if (!shouldStart || !cardsCollection) return

    const driverObj = createOnboardingTourDriver({
      nextBtnText: t('Next'),
      onCloseClick: () => {
        resumeStepRef.current = driverObj.getActiveStep() ?? null
        driverObj.destroy()
        setIsFinishConfirmOpen(true)
      },
    })
    driverInstanceRef.current = driverObj

    const { cleanup, highlightStep, steps } = createListTourSteps({
      cardsCollection,
      driverObj,
      onTourCompleted: () => {
        cleanup()
        driverObj.destroy()
        driverInstanceRef.current = null
      },
      t,
    })

    const resumeStepIndex = resolveTourResumeStepIndex(steps.length, listTour.stepIndex)
    if (resumeStepIndex == null) {
      cleanup()
      driverObj.destroy()
      driverInstanceRef.current = null
      return
    }

    const startStep = steps[resumeStepIndex]
    if (!startStep) {
      cleanup()
      driverObj.destroy()
      driverInstanceRef.current = null
      return
    }

    const layoutTimeoutId = setTimeout(() => {
      highlightStep(startStep, resumeStepIndex)
    }, WAIT_FOR_LAYOUT_MS)

    return () => {
      clearTimeout(layoutTimeoutId)
      cleanup()
      driverObj.destroy()
      driverInstanceRef.current = null
    }
    // Resume from persisted stepIndex only on mount; avoid restarting when stepIndex advances mid-tour.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-once for driver lifecycle
  }, [shouldStart, cardsCollection, t])

  if (!shouldStart) return null

  return (
    <TourFinishConfirmDrawer
      onClose={handleKeepExploring}
      onEndTour={handleEndTour}
      onKeepExploring={handleKeepExploring}
      open={isFinishConfirmOpen}
    />
  )
}
